import React, { useState, useRef, useEffect } from 'react';
import { X, CheckCircle, Camera } from 'lucide-react';
import jsQR from 'jsqr';

const QRScanner = ({ onScan, onClose }) => {
  const [manualId, setManualId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  // Lister les caméras disponibles
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        console.log('📹 Caméras trouvées:', videoDevices);
        setCameras(videoDevices);
        if (videoDevices.length > 0) {
          setSelectedCamera(videoDevices[0].deviceId);
        }
      })
      .catch(err => {
        console.error('Erreur liste caméras:', err);
      });
  }, []);

  const startScanning = async () => {
    try {
      setError('');
      console.log('🎥 Demande accès caméra:', selectedCamera);
      
      // Obtenir le flux vidéo de la caméra sélectionnée
      const constraints = {
        video: {
          deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Stream caméra obtenu');
      streamRef.current = stream;
      
      // Activer scanning pour afficher la vidéo
      setScanning(true);
      
      // Attendre que React crée l'élément vidéo dans le DOM
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.play().then(() => {
            console.log('✅ Vidéo démarrée et lecture en cours');
            
            // Démarrer le scan QR code avec jsQR
            scanIntervalRef.current = setInterval(() => {
              if (videoRef.current && canvasRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                try {
                  const video = videoRef.current;
                  const canvas = canvasRef.current;
                  const context = canvas.getContext('2d');
                  
                  // Définir la taille du canvas
                  canvas.width = video.videoWidth;
                  canvas.height = video.videoHeight;
                  
                  if (canvas.width > 0 && canvas.height > 0) {
                    // Dessiner la frame vidéo sur le canvas
                    context.drawImage(video, 0, 0, canvas.width, canvas.height);
                    
                    // Obtenir les données d'image
                    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                    
                    // Decoder avec jsQR
                    const code = jsQR(imageData.data, imageData.width, imageData.height, {
                      inversionAttempts: 'dontInvert',
                    });
                    
                    if (code && code.data) {
                      console.log('✅ QR Code détecté:', code.data);
                      stopScanning();
                      onScan(code.data);
                    }
                  }
                } catch (err) {
                  console.error('Erreur scan:', err);
                }
              }
            }, 250);
            
          }).catch(err => {
            console.error('❌ Erreur play vidéo:', err);
            setError(`Erreur lecture vidéo: ${err.message}`);
          });
        } else {
          console.error('❌ videoRef ou stream non disponible');
          setError('Élément vidéo non disponible');
        }
      }, 150);
      
    } catch (err) {
      console.error('❌ Erreur caméra:', err);
      setError(`Erreur accès caméra: ${err.message}`);
      setScanning(false);
    }
  };

  const stopScanning = () => {
    console.log('🛑 Arrêt du scanner');
    
    // Arrêter l'intervalle de scan
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    // Arrêter le stream vidéo
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('🛑 Track vidéo arrêté');
      });
      streamRef.current = null;
    }
    
    // Réinitialiser la vidéo
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const handleManualSubmit = () => {
    if (manualId.trim()) {
      onScan(manualId.trim());
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
              Scanner QR Code
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          {/* Sélecteur de caméra */}
          {!scanning && cameras.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choisir la caméra :
              </label>
              <select
                value={selectedCamera}
                onChange={(e) => setSelectedCamera(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                {cameras.map((camera, index) => (
                  <option key={camera.deviceId} value={camera.deviceId}>
                    {camera.label || `Caméra ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Zone vidéo */}
          <div className="mb-6">
            {!scanning ? (
              <button
                onClick={startScanning}
                disabled={cameras.length === 0}
                className="w-full py-4 text-white rounded-lg flex items-center justify-center gap-2 text-lg hover:opacity-90 transition disabled:opacity-50"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              >
                <Camera className="w-6 h-6" />
                {cameras.length === 0 ? 'Aucune caméra détectée' : 'Activer la caméra'}
              </button>
            ) : (
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    height: 'auto',
                    minHeight: '400px',
                    maxHeight: '500px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
                
                {/* Canvas caché pour le décodage QR */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                
                {/* Cadre de scan */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="relative" style={{ width: '250px', height: '250px' }}>
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-green-500"></div>
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-green-500"></div>
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-green-500"></div>
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-green-500"></div>
                  </div>
                </div>
                
                <button
                  onClick={stopScanning}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition z-10"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {scanning && (
              <p className="text-center mt-3 text-sm text-gray-700 font-medium">
                📱 Positionnez le QR code dans le cadre vert
              </p>
            )}
          </div>

          {/* Manual Input */}
          <div className="border-t pt-4">
            <p className="text-sm text-gray-600 mb-3">Ou entrez manuellement le numéro de réservation :</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleManualSubmit()}
                placeholder="UUID de réservation"
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
              <button
                onClick={handleManualSubmit}
                className="px-6 py-2 text-white rounded-lg flex items-center gap-2"
                style={{ backgroundColor: 'rgb(34, 197, 94)' }}
              >
                <CheckCircle className="w-4 h-4" />
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
